import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addMessageReaction,
  fetchConversationSummary,
  fetchConversations,
  fetchMessages,
  findOrCreateDirectConversation,
  pollMessages,
  postMessage,
  removeMessageReaction,
  type ChatMessage,
  type ConversationListItem,
  type ConversationSummary,
  type DirectConversationPayload,
  type SendMessagePayload,
} from "../api/chatApi";
import { applyReactionToggleOptimistic } from "../lib/chatReactionOptimistic";
import { useAuthStore } from "../stores/auth.store";



export const useConversations = () => {
  return useQuery<ConversationListItem[]>({
    queryKey: ["conversations"],
    queryFn: fetchConversations,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}



export const useFindOrCreateDirectConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DirectConversationPayload) =>
      findOrCreateDirectConversation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}



export const useConversationSummary = (conversationId: string | undefined) => {
  return useQuery<ConversationSummary>({
    queryKey: ["conversation-summary", conversationId],
    queryFn: () => fetchConversationSummary(conversationId ?? ""),
    enabled: Boolean(conversationId),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}



export const useChat = (conversationId: string | undefined) => {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const queryKey = useMemo(
    () => ["messages", conversationId] as const,
    [conversationId],
  );

  const messagesQuery = useQuery({
    queryKey,
    queryFn: () => fetchMessages(conversationId ?? ""),
    enabled: Boolean(conversationId),
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    /** First poll omits `after` so the server returns a cursor immediately (avoids client/server clock skew). */
    let after: string | undefined;

    const run = async () => {
      while (!cancelled) {
        try {
          const result = await pollMessages(conversationId, after);
          after = result.updatedAt;
          if (result.hasUpdates) {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: ["conversations"] });
          }
        } catch {
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [conversationId, queryClient, queryKey]);

  /** Chaîne les POST pour ne pas remplacer la mutation en cours (comportement de useMutation.mutate). */
  const sendChainRef = useRef(Promise.resolve());
  const [pendingSendCount, setPendingSendCount] = useState(0);

  const sendMessageAsync = useCallback(
    async (newMessage: SendMessagePayload): Promise<ChatMessage> => {
      if (!conversationId || !currentUserId) {
        throw new Error("Conversation ou utilisateur indisponible");
      }

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) => [
        ...old,
        {
          id: tempId,
          conversationId,
          senderId: currentUserId ?? null,
          senderClientId: null,
          senderLabel: null,
          content: newMessage.content,
          createdAt: new Date().toISOString(),
          status: "sending",
        },
      ]);

      setPendingSendCount((c) => c + 1);

      const run = async (): Promise<ChatMessage> => {
        try {
          const serverMessage = await postMessage(conversationId, newMessage);
          queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) =>
            old.map((m) => (m.id === tempId ? serverMessage : m)),
          );
          return serverMessage;
        } catch (e) {
          queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) =>
            old.filter((m) => m.id !== tempId),
          );
          throw e;
        } finally {
          setPendingSendCount((c) => Math.max(0, c - 1));
          queryClient.invalidateQueries({ queryKey });
          queryClient.invalidateQueries({ queryKey: ["conversations"] });
          queryClient.invalidateQueries({
            queryKey: ["conversation-summary", conversationId],
          });
        }
      };

      const task = sendChainRef.current.then(() => run());
      sendChainRef.current = task.then(
        () => undefined,
        () => undefined,
      );
      return task;
    },
    [conversationId, currentUserId, queryClient, queryKey],
  );

  const sendMessage = useCallback(
    (newMessage: SendMessagePayload) => {
      void sendMessageAsync(newMessage);
    },
    [sendMessageAsync],
  );

  const toggleReactionMutation = useMutation({
    mutationFn: async (args: {
      messageId: string;
      emoji: string;
      reactedByMe: boolean;
    }) => {
      if (args.reactedByMe) {
        return removeMessageReaction(conversationId ?? "", args.messageId, {
          emoji: args.emoji,
        });
      }
      return addMessageReaction(conversationId ?? "", args.messageId, {
        emoji: args.emoji,
      });
    },
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChatMessage[]>(queryKey);
      queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) =>
        applyReactionToggleOptimistic(
          old,
          args.messageId,
          args.emoji,
          args.reactedByMe,
        ),
      );
      return { previous };
    },
    onError: (_err, _args, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ["conversation-summary", conversationId],
      });
    },
  });

  return {
    messages: messagesQuery.data ?? [],
    isLoading: messagesQuery.isLoading,
    isFetching: messagesQuery.isFetching,
    sendMessage,
    sendMessageAsync,
    isSending: pendingSendCount > 0,
    toggleReaction: toggleReactionMutation.mutate,
    isTogglingReaction: toggleReactionMutation.isPending,
  };
}
